import type { Directive } from "vue";

let particles: Particle[] = [];
let animationId: number;

const colors = ["#FFD700", "#FF6B8A", "#7ECFC0", "#C4B5FD", "#FFA726", "#EF5350", "#AB47BC", "#66BB6A", "#42A5F5", "#FFCA28"];

class Particle {
	x: number;
	y: number;
	vx: number;
	vy: number;
	color: string;
	size: number;
	rotation: number;
	rotationSpeed: number;
	gravity: number;
	friction: number;
	opacity: number;
	fadeSpeed: number;
	shape: string;
	width: number;
	height: number;
	ctx: CanvasRenderingContext2D;

	constructor(ctx: CanvasRenderingContext2D) {
		this.x = window.innerWidth / 2;
		this.y = window.innerHeight / 2;
		const angle = Math.random() * Math.PI * 2;
		const speed = 4 + Math.random() * 8;
		this.vx = Math.cos(angle) * speed;
		this.vy = Math.sin(angle) * speed - 4;
		this.color = colors[Math.floor(Math.random() * colors.length)];
		this.size = 4 + Math.random() * 6;
		this.rotation = Math.random() * 360;
		this.rotationSpeed = (Math.random() - 0.5) * 12;
		this.gravity = 0.12 + Math.random() * 0.05;
		this.friction = 0.98;
		this.opacity = 1;
		this.fadeSpeed = 0.005 + Math.random() * 0.008;
		this.shape = Math.random() > 0.5 ? "rect" : "circle";
		this.width = this.shape === "rect" ? this.size * (0.4 + Math.random() * 0.6) : this.size;
		this.height = this.shape === "rect" ? this.size * (0.8 + Math.random() * 0.4) : this.size;
		this.ctx = ctx;
	}

	update() {
		this.vy += this.gravity;
		this.vx *= this.friction;
		this.vy *= this.friction;
		this.x += this.vx;
		this.y += this.vy;
		this.rotation += this.rotationSpeed;
		this.opacity -= this.fadeSpeed;
	}

	draw() {
		if (!this.ctx) return;
		this.ctx.save();
		this.ctx.translate(this.x, this.y);
		this.ctx.rotate((this.rotation * Math.PI) / 180);
		this.ctx.globalAlpha = Math.max(0, this.opacity);
		this.ctx.fillStyle = this.color;

		if (this.shape === "rect") {
			this.ctx.fillRect(-this.width / 2, -this.height / 2, this.width, this.height);
		} else {
			this.ctx.beginPath();
			this.ctx.arc(0, 0, this.size / 2, 0, Math.PI * 2);
			this.ctx.fill();
		}

		this.ctx.restore();
	}
}

export const vConfetti: Directive<HTMLCanvasElement & { _resize: () => void }> = {
	mounted(el) {
		const ctx = el.getContext("2d")!;

		const animate = () => {
			ctx.clearRect(0, 0, window.innerWidth, window.innerHeight);

			for (const p of particles) {
				p.update();
				p.draw();
			}

			particles = particles.filter((p) => p.opacity > 0);
			if (particles.length > 0) animationId = requestAnimationFrame(animate);
		};

		el._resize = () => {
			const dpr = window.devicePixelRatio || 1;
			el.width = window.innerWidth * dpr;
			el.height = window.innerHeight * dpr;
			ctx.scale(dpr, dpr);
		};
		el._resize();
		window.addEventListener("resize", el._resize);

		setTimeout(() => {
			for (let i = 0; i < 90; i++) particles.push(new Particle(ctx));
			animate();
		}, 400);

		// Second smaller burst
		setTimeout(() => {
			for (let i = 0; i < 40; i++) particles.push(new Particle(ctx));
		}, 900);
	},
	unmounted(el) {
		window.removeEventListener("resize", el._resize);
		if (animationId) cancelAnimationFrame(animationId);
		particles = [];
	},
};
